import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { Operation } from 'data/types';
import * as selectors from 'data/xtp-xdp-connection/xtp-xdp-connection.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { adaptToCreate } from 'data/xtp-xdp-connection/xtp-xdp-connection.adapters';
import * as xtpXdpConnectionActions from 'data/xtp-xdp-connection/xtp-xdp-connection.actions';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import { XtpXdpConnection, newXtp, newXtpXdpConnectionProtocol } from 'data/xtp-xdp-connection/xtp-xdp-connection.types';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import XtpXdpConnectionForm from '../../components/XtpXdpConnectionForm';
import { Ref } from '../../components/XtpXdpConnectionForm/XtpXdpConnectionForm';
import { xtpXdpConnectionTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFormData,
  adaptXtpXdpConnectionTemplate,
} from '../../utils/xtpXdpConnectionTemplate.adapter';
import {
  getAuditDetailsToSubmit,
  getProtocolAuditDetailsToSubmit,
  getCFData,
  getProtocolsDataToSubmit,
} from '../../utils/xtpXdpConnectionDataToSubmit';
import { CONNECTION_PROTOCOLS_TYPES, defaultProtocolPorts } from '../../XtpXdpConnection.types';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  xdpTemplateDef: adaptTemplateVisibilityMode(
    xtpXdpConnectionTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  xdps: xdpSelectors.getXdps(state),
  xtps: xtpSelectors.getXtps(state),
  xtpXdpConnections: selectors.getXtpXdpsConnectionList(state),
});

const mapDispatch = {
  createXtpXdpConnection: xtpXdpConnectionActions.createXtpXdpConnection,
  goToXdpList: xtpXdpConnectionActions.goToXtpXdpConnectionList,
  getXdps: xdpActions.getXdps,
  getXtps: xtpActions.getXtps,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const XtpXdpConnectionCreate = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  xdpTemplateDef,
  xdps,
  xtps,
  xtpXdpConnections,
  createXtpXdpConnection,
  goToXdpList,
  getXtps,
  getXdps,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<XtpXdpConnection>(
    cloneDeep(newXtp),
  );
  const [template, setTemplate] = useState(xdpTemplateDef);
  const [ifId, setIfId] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    getXdps(deliveryFunctionId);
    getXtps(deliveryFunctionId);
  }, [getXdps, getXtps, deliveryFunctionId]);

  useEffect(() => {
    setTemplate(
      adaptXtpXdpConnectionTemplate(
        xdpTemplateDef as FormTemplate,
        xdps,
        xtps
      )
    );
  }, [xdpTemplateDef, xdps, xtps, xtpXdpConnections]);

  const handleFieldUpdate = (
    { name: fieldName, value }: FieldUpdateHandlerParam,
    fieldSection?: 'main' | 'protocol' | 'protocolInterface',
    protolType?: string,
  ) => {
    if (template == null || formData == null) {
      return;
    }

    if (fieldSection === 'protocol') {
      handleProcolSectionFieldUpdate(fieldName, value, formData, template);
    } else if (fieldSection === 'protocolInterface') {
      handleProcolInterfaceSectionFieldUpdate(fieldName, value, formData, template, protolType);
    } else {
      handleMainSectionFieldUpdate(fieldName, value, formData, template);
    }
  };

  const handleMainSectionFieldUpdate = (
    fieldName: string,
    value: string,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
  ) => {
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);

    let newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    newData = adaptFormData(newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  }

  const handleProcolSectionFieldUpdate = (
    fieldName: string,
    value: string | boolean,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
  ) => {
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);

    const key = fieldName as keyof typeof CONNECTION_PROTOCOLS_TYPES;
    const newIfId = ifId + 1;

    const type = CONNECTION_PROTOCOLS_TYPES[key];
    clonedFormData.protocols[fieldName] = {
      ...newXtpXdpConnectionProtocol,
      type,
      destPort: defaultProtocolPorts[type] || '',
      ifid: newIfId.toString(),
      operation: value === true ? 'CREATE' : 'DELETE',
    }

    setIfId(newIfId);
    setFormData(clonedFormData)
    setTemplate(clonedTemplate);
  }

  const handleProcolInterfaceSectionFieldUpdate = (
    fieldName: string,
    value: boolean,
    form: XtpXdpConnection,
    formTemplate: FormTemplate,
    protocolType?: string,
  ) => {
    if (!protocolType) {
      return;
    }
    const clonedFormData = cloneDeep(form);
    const clonedTemplate = cloneDeep(formTemplate);
    const protocolData = clonedFormData.protocols[protocolType];
    if (protocolData == null) {
      return;
    }

    setFormData((current) => {
      return {
        ...current,
        protocols: {
          ...current.protocols,
          [protocolType]: {
            ...protocolData,
            [fieldName]: value
          }
        }
      }
    });

    setTemplate(clonedTemplate);
  }

  const getCreateXtpXdpConnectionActionPayload = () => {

    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const xtpXdpFormData = formRef.current.getValues();

    const connectionPayload = {
      data: getCFData(xtpXdpFormData.main),
      auditDetails: getAuditDetailsToSubmit(
        xtpXdpFormData.main,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    const protocolsPayload = getProtocolsDataToSubmit(xtpXdpFormData)
      .map((item) => ({
        data: {
          ...item,
          ipduid: xtpXdpFormData.main.ipduid,
        },
        auditDetails: getProtocolAuditDetailsToSubmit(
          item,
          null,
          isAuditEnabled,
          userId,
          userName,
          'CREATE',
        ),
      }))

    return {
      main: connectionPayload,
      protocols: protocolsPayload,
    };
  };

  const handleClickSave = () => {
    const payload = getCreateXtpXdpConnectionActionPayload();
    if (payload) {
      createXtpXdpConnection(deliveryFunctionId, payload.main, payload.protocols, history);
    }
  };

  const handleCancel = () => goToXdpList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <XtpXdpConnectionForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="XtpXdpConnectionCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('xtpXdpConnection.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>{renderFormPanel('')}</FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(XtpXdpConnectionCreate));
